/**
 * searchWidgets.tsx — Widgets de INTELIGENCIA de zona para el escritorio de
 * Búsqueda (conviven con el dnd-kit existente). Computan agregados EN FRONTEND
 * sobre los resultados cargados (`items`), reutilizando `homeAggregates` y
 * `opportunityScore`. No tocan la lógica del hook `useSearchAndInbox`.
 */
import { useMemo, useState } from 'react'
import type { InboxItem, InboxFilters } from '@/services/listingsService'
import { computeHomeAggregates } from '@/lib/homeAggregates'
import { opportunityScore, contactability } from '@/lib/opportunityScore'
import MapZones from '@/components/cabina/MapZones'
import MapExpandOverlay from '@/components/cabina/MapExpandOverlay'
import { useInboxMapItems } from '@/pages/searchShared'

/** Botón "Expandir" superpuesto al mini-mapa → abre el mapa a pantalla completa. */
function ExpandMapButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="map-expand-btn" onClick={onClick} title="Ver mapa grande">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 3H5a2 2 0 00-2 2v4M15 3h4a2 2 0 012 2v4M9 21H5a2 2 0 01-2-2v-4M15 21h4a2 2 0 002-2v-4" />
      </svg>
      Expandir
    </button>
  )
}

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 } as const

export function useResultAggregates(items: InboxItem[]) {
  return useMemo(() => {
    const agg = computeHomeAggregates(items, items.length)
    const scores = items.map((i) => opportunityScore(i).score)
    const oppAvg = scores.length
      ? Math.round(scores.reduce((s, n) => s + n, 0) / scores.length)
      : 0
    const contacts = items.map((i) => contactability(i))
    const withEmail = contacts.filter((c) => c.email).length
    const withPhone = contacts.filter((c) => c.phone).length
    const claimed = items.filter((i) => i.is_claimed === true).length
    return { agg, oppAvg, n: items.length, withEmail, withPhone, claimed }
  }, [items])
}

/** Mapa real de la zona con pins por municipio coloreados por oportunidad.
 *  El mini-mapa usa la página (`items`); al EXPANDIR, el overlay muestra TODOS
 *  los leads del usuario (dataset completo con los filtros activos). */
export function ZoneMapWidget({ items, filters }: { items: InboxItem[]; filters: InboxFilters }) {
  const { agg, n } = useResultAggregates(items)
  const [expanded, setExpanded] = useState(false)
  const overlayItems = useInboxMapItems(filters, expanded, items)
  return (
    <div>
      <div className="mini-map-shell">
        <MapZones items={items} height={150} showLegend={false} />
        {n > 0 && <ExpandMapButton onClick={() => setExpanded(true)} />}
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 9 }}>
        {n} leads · {agg.zones.length} {agg.zones.length === 1 ? 'municipio' : 'municipios'} en la zona
      </div>
      <MapExpandOverlay open={expanded} items={overlayItems} onClose={() => setExpanded(false)} />
    </div>
  )
}

function MiniBars({ rows, max }: { rows: { label: string; count: number }[]; max: number }) {
  return (
    <div className="bars-mini">
      {rows.map((r) => (
        <div className="bm" key={r.label}>
          <span className="l" title={r.label}>{r.label}</span>
          <div className="t"><i style={{ width: `${max ? (r.count / max) * 100 : 0}%` }} /></div>
          <span className="n">{r.count}</span>
        </div>
      ))}
    </div>
  )
}

/** Distribución de los resultados por ciudad y por categoría. */
export function ResultsDistWidget({ items }: { items: InboxItem[] }) {
  const { agg } = useResultAggregates(items)
  const cities = agg.zones.slice(0, 4).map((z) => ({ label: z.city, count: z.count }))
  const cats = agg.categories.slice(0, 4).map((c) => ({ label: c.label, count: c.count }))
  const maxCity = Math.max(1, ...cities.map((c) => c.count))
  const maxCat = Math.max(1, ...cats.map((c) => c.count))
  if (items.length === 0) return <div className="empty-hint">Sin resultados que distribuir.</div>
  return (
    <div>
      <div className="panel-label" style={{ margin: '0 0 8px 0' }}>Por ciudad</div>
      <div style={{ marginBottom: 14 }}><MiniBars rows={cities} max={maxCity} /></div>
      <div className="panel-label" style={{ margin: '0 0 8px 0' }}>Por categoría</div>
      <MiniBars rows={cats} max={maxCat} />
    </div>
  )
}

/** Calidad de contacto + valoración media de los resultados de la búsqueda. */
export function SelectionQualityWidget({ items }: { items: InboxItem[] }) {
  const { agg, oppAvg, n, withEmail, withPhone, claimed } = useResultAggregates(items)
  if (n === 0) return <div className="empty-hint">Sin resultados que analizar.</div>
  const ratingTxt = agg.rating.avg ? agg.rating.avg.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'
  return (
    <div>
      <div className="kpi-grid" style={{ marginBottom: 12 }}>
        <div className="kpi lead"><div className="k-label">Oportunidad media</div><div className="k-val">{oppAvg}</div></div>
        <div className="kpi"><div className="k-label">Valoración</div><div className="k-val">{ratingTxt}★</div></div>
        <div className="kpi"><div className="k-label">Reseñas Σ</div><div className="k-val">{agg.rating.totalReviews.toLocaleString('es-ES')}</div></div>
      </div>
      <div className="contact-dot"><span className="ci off"><svg viewBox="0 0 24 24" {...stroke}><path d="M3 8l9 6 9-6" /><rect x="3" y="5" width="18" height="14" rx="2" /></svg></span><span className="cn">Con email</span><span className="cp">{withEmail} / {n}</span></div>
      <div className="contact-dot"><span className="ci on"><svg viewBox="0 0 24 24" {...stroke}><path d="M5 4h4l2 5-3 2a12 12 0 006 6l2-3 5 2v4a2 2 0 01-2 2A18 18 0 013 6a2 2 0 012-2z" /></svg></span><span className="cn">Con teléfono</span><span className="cp">{withPhone} / {n}</span></div>
      <div className="contact-dot"><span className="ci off"><svg viewBox="0 0 24 24" {...stroke}><path d="M12 2l2.4 7.4H22l-6 4.5 2.3 7.1L12 16.8 5.7 21l2.3-7.1-6-4.5h7.6z" /></svg></span><span className="cn">Reclamados</span><span className="cp">{claimed} / {n}</span></div>
    </div>
  )
}

/** Teaser premium de inteligencia de zona (próximamente / bloqueado). */
export function ZoneIntelPremiumWidget() {
  return (
    <div className="intel-list">
      <div className="intel" style={{ gridTemplateColumns: '1fr auto' }}>
        <div><div className="i-name">Web lenta / antigua</div><div className="i-desc">% de la zona</div></div>
        <span className="skel" />
      </div>
      <div className="intel" style={{ gridTemplateColumns: '1fr auto' }}>
        <div><div className="i-name">Sin Google Ads</div><div className="i-desc">oportunidad SEM</div></div>
        <span className="skel" />
      </div>
      <div className="intel" style={{ gridTemplateColumns: '1fr auto' }}>
        <div><div className="i-name">Visibilidad SEO baja</div><div className="i-desc">posición media</div></div>
        <span className="lock"><svg viewBox="0 0 24 24" {...stroke}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg></span>
      </div>
    </div>
  )
}
